import { currentUrl, offsetTop, useMatchLargeViewport } from '@avensia/excite';
import { useFreezeScroll } from '@avensia/excite-ui';
import * as React from 'react';
import { DESKTOP_HEADER_HEIGHT, HEADER_HEIGHT } from './utils/constants';

export enum ScrollDirection {
  Down,
  Up,
}

export function useShowElementOnScrollUp<T extends HTMLElement>(
  containerRef: React.RefObject<T | null>,
  elementRef: React.RefObject<T | null>,
  freezeScroll: boolean,
): [boolean, boolean] {
  const prevScrollPosition = React.useRef<number>(0);
  const previousDirection = React.useRef<ScrollDirection>(undefined);
  const [, forceUpdate] = React.useReducer(x => x + 1, 0);
  const isDesktop = useMatchLargeViewport();
  const pageUrl = React.useRef<string>('');
  const isShown = React.useRef(true);

  const currentUrlHref = currentUrl().href;
  React.useEffect(() => {
    if (pageUrl.current !== currentUrlHref) {
      pageUrl.current = currentUrlHref;
      isShown.current = true;
    }
  }, [currentUrlHref]);

  const isAboveContainer = React.useRef<boolean>(true);

  useFreezeScroll(freezeScroll);

  const onScrollDirectionChange = React.useCallback(() => {
    if (freezeScroll) {
      return;
    }

    const scrollPosition = window.scrollY;
    const dir = scrollPosition > prevScrollPosition.current ? ScrollDirection.Down : ScrollDirection.Up;

    const diff = scrollPosition - prevScrollPosition.current;

    const isScrolling = diff > 0 || diff < 0;
    const containerPositionY = containerRef.current
      ? offsetTop(containerRef.current) - (isDesktop ? DESKTOP_HEADER_HEIGHT : HEADER_HEIGHT)
      : 0;

    isAboveContainer.current = scrollPosition <= containerPositionY;
    const elementHeight = elementRef.current?.offsetHeight || 0;
    const isAboveElement = scrollPosition <= containerPositionY - elementHeight;

    if (isAboveElement) {
      forceUpdate();
    } else if (!isAboveContainer.current && previousDirection.current !== dir && isScrolling) {
      isShown.current = dir === ScrollDirection.Up;
      forceUpdate();
      previousDirection.current = dir;
    }

    prevScrollPosition.current = scrollPosition;
  }, [freezeScroll, containerRef, isDesktop, elementRef]);

  React.useEffect(() => {
    window.addEventListener('scroll', onScrollDirectionChange, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScrollDirectionChange);
    };
  }, [onScrollDirectionChange]);

  return [isShown.current, !isAboveContainer.current && isShown.current];
}
